/**
 * mapProductToShareTarget — normalizes a ProducerProduct into a ShareTarget.
 * Canonical data sources:
 *   - producer_products → title, price_ore, description
 *   - media_assets → cover/gallery image (single source of truth)
 *   - producers → name, slug, id
 *
 * Image resolution priority (handled by caller via useCoverAsset / useProductCoverImages):
 *   1. product cover asset (media_assets kind='cover')
 *   2. product gallery asset (media_assets kind='gallery')
 *   3. null → Share Studio renders branded fallback
 */

import type { ProductShareTarget } from '../ShareTarget';

interface ProductMapperInput {
  product: {
    id: string;
    title: string;
    price_ore: number;
    description?: string | null;
  };
  /** Resolved from media_assets — NOT from legacy product.images */
  coverImageUrl: string | null;
  producerName: string;
  producerSlug: string;
  producerId: string;
  /** Optional place/venue name shown on the share card */
  placeName?: string | null;
  /** Optional delivery/pickup snippet */
  deliverySnippet?: string | null;
}

export function mapProductToShareTarget(input: ProductMapperInput): ProductShareTarget {
  const { product, coverImageUrl, producerName, producerSlug, producerId, placeName, deliverySnippet } = input;

  return {
    type: 'product',
    id: product.id,
    slug: null,
    title: product.title,
    heroImageUrl: coverImageUrl,
    producerName,
    producerSlug,
    producerId,
    priceOre: product.price_ore,
    placeName: placeName ?? null,
    deliverySnippet: deliverySnippet ?? null,
    descriptionExcerpt: product.description
      ? product.description.slice(0, 140).trim()
      : null,
  };
}
